import type { WalletKind } from './wallet';

/**
 * Watches the injected Phantom provider for the connected account drifting
 * away from the wallet bound to the current backend session. Many dApps force
 * a fresh sign-in the moment the wallet switches; without it the UI keeps
 * showing account A while Phantom would sign (and pay) as account B.
 *
 * Detection is two-layered:
 *  - provider `accountChanged` / `disconnect` events (primary), and
 *  - a passive publicKey comparison on window focus / tab visibility
 *    (catches events missed while the tab was backgrounded).
 *
 * The watcher only ever LOGS OUT — it never silently rebinds the session to
 * the new wallet. A fresh signed login challenge (authFlow.ts) is the only
 * path to authenticating the new account; server-side session/payment
 * binding stays authoritative regardless of anything this file does.
 */

export const WALLET_CHANGED_MESSAGE = 'Wallet changed. Please sign in again.';

/** The slice of provider surface the watcher needs (Phantom-compatible). */
export interface WatchedProvider {
  publicKey?: { toString(): string } | null;
  on?(event: 'accountChanged' | 'disconnect', handler: (payload?: unknown) => void): void;
}

/** The slice of session state the watcher compares against. */
export interface WalletSessionSnapshot {
  hasSession: boolean;
  walletKind: WalletKind | null;
  walletAddress: string | null;
}

export type WalletCheckVerdict = 'ignore' | 'logout';

function normalizeAddress(value: unknown): string | null {
  if (value === null || value === undefined) return null;
  const address = String(value).trim();
  return address.length > 0 ? address : null;
}

/** Only Phantom-backed sessions react to Phantom account churn (dev keypair sessions never do). */
function isPhantomSession(session: WalletSessionSnapshot): boolean {
  return session.hasSession && session.walletKind === 'phantom' && !!session.walletAddress;
}

/**
 * Verdict for an explicit `accountChanged`/`disconnect` signal. A null/absent
 * publicKey here is a positive "no longer this account" signal (switched to a
 * not-connected account, or disconnected) — it logs out, matching the event
 * semantics dApps get from Phantom.
 */
export function verdictForAccountChanged(session: WalletSessionSnapshot, newAddress: unknown): WalletCheckVerdict {
  if (!isPhantomSession(session)) return 'ignore';
  const next = normalizeAddress(newAddress);
  if (next !== null && next === normalizeAddress(session.walletAddress)) return 'ignore';
  return 'logout';
}

/**
 * Verdict for the passive focus/visibility comparison. Unlike the event path,
 * a null provider publicKey is NOT evidence of a switch — Phantom simply may
 * not have auto-connected this tab yet (e.g. right after a page reload with a
 * persisted session), so only a present-and-different key logs out.
 */
export function verdictForFocusCheck(session: WalletSessionSnapshot, providerAddress: unknown): WalletCheckVerdict {
  if (!isPhantomSession(session)) return 'ignore';
  const current = normalizeAddress(providerAddress);
  if (current === null || current === normalizeAddress(session.walletAddress)) return 'ignore';
  return 'logout';
}

export interface WalletWatcherOptions {
  getProvider(): WatchedProvider | null;
  getSession(): WalletSessionSnapshot;
  /** Fired once per mismatch: perform logout + user messaging here. */
  onWalletChanged(): void;
  /** Injectable for tests; defaults wire window focus + document visibility. */
  addWakeListener?(handler: () => void): void;
  isDocumentVisible?(): boolean;
}

function defaultAddWakeListener(handler: () => void): void {
  window.addEventListener('focus', handler);
  document.addEventListener('visibilitychange', handler);
}

export class WalletWatcher {
  private started = false;
  private providerListenersAttached = false;
  private notifying = false;

  constructor(private readonly options: WalletWatcherOptions) {}

  /**
   * Idempotent. Registers wake listeners once; provider listeners attach
   * lazily (retried on every wake) because Phantom may inject after startup.
   */
  start(): void {
    if (this.started) return;
    this.started = true;
    this.attachProviderListeners();
    (this.options.addWakeListener ?? defaultAddWakeListener)(() => this.onWake());
  }

  private onWake(): void {
    this.attachProviderListeners();
    if ((this.options.isDocumentVisible ?? (() => !document.hidden))()) this.checkNow();
  }

  private attachProviderListeners(): void {
    if (this.providerListenersAttached) return;
    const provider = this.options.getProvider();
    if (!provider?.on) return;
    provider.on('accountChanged', (publicKey) => this.handleAccountChanged(publicKey));
    provider.on('disconnect', () => this.handleAccountChanged(null));
    this.providerListenersAttached = true;
  }

  /** Exposed for tests; event payload is Phantom's PublicKey (or null). */
  handleAccountChanged(publicKey: unknown): void {
    if (verdictForAccountChanged(this.options.getSession(), publicKey) === 'logout') this.fire();
  }

  /** Compare the live provider key against the session wallet right now. */
  checkNow(): void {
    const provider = this.options.getProvider();
    if (!provider) return;
    if (verdictForFocusCheck(this.options.getSession(), provider.publicKey ?? null) === 'logout') this.fire();
  }

  /**
   * Single-fire guard: onWalletChanged logs out synchronously (clearing the
   * session), so any queued duplicate event re-checks against hasSession=false
   * and no-ops — this flag only covers re-entrancy during the callback itself.
   */
  private fire(): void {
    if (this.notifying) return;
    this.notifying = true;
    try {
      this.options.onWalletChanged();
    } finally {
      this.notifying = false;
    }
  }
}
